import { fileModule } from "@/modules/file";
import { DropItem } from "react-aria-components";
import { toastModule } from "@/modules/toast";
import { TrackedEvent } from "@/domains/metrics";
import { trackEvent } from "@/domains/metrics";
import styled from "@emotion/styled";
import { importFilesAsSession } from "@/modules/file/import";
import { type AppStore } from "@/store";
import { EventContext } from "@/domains/metrics/context";
import { MDSIcon, MDSIconKind } from "@/mds";
import { logger } from "../logger";

const MAX_FILES = 1_000;
const MAX_FILE_SIZE = 30 * 1024 * 1024; // set to match the limit on dropped files in Common Editor

export const importDroppedItems = async (
  items: DropItem[],
  store: AppStore,
  context: EventContext
) => {
  // Recursively get all file items from e.items
  const allFileItems = await fileModule.collectFileDropItems({ items });

  // Get file objects and their sizes
  const fileItemsWithSizes = await Promise.all(
    allFileItems.map(async fileItem => {
      const file = await fileItem.getFile();
      return { fileItem, size: file.size, type: fileItem.type, name: fileItem.name };
    })
  );

  const bigFilesCount = fileItemsWithSizes.filter(({ size }) => size > MAX_FILE_SIZE).length;

  // Helper to check if a file is an importable type
  const isImportableType = (name: string, type: string) =>
    name.toLowerCase().endsWith(".md") ||
    name.toLowerCase().endsWith(".txt") ||
    type === "text/markdown" ||
    type === "text/plain";

  // Split files into importable and filtered-out in a single pass
  const importableFileItems: (typeof fileItemsWithSizes)[number]["fileItem"][] = [];
  const filteredOutExtensions: string[] = [];

  for (const { fileItem, name, type, size } of fileItemsWithSizes) {
    const isImportable = isImportableType(name, type);
    const isTooBig = size > MAX_FILE_SIZE;
    const lastDotIndex = name.lastIndexOf(".");

    // Log extensions of non-importable files
    if (!isImportable) {
      filteredOutExtensions.push(lastDotIndex !== -1 ? name.slice(lastDotIndex) : "[no_extension]");
    }

    // Only include files that are importable AND not too big
    if (isImportable && !isTooBig) {
      importableFileItems.push(fileItem);
    }
  }

  const tooManyFiles = importableFileItems.length > MAX_FILES;

  // Dedupe filtered out extensions
  const uniqueFilteredOutExtensions = [...new Set(filteredOutExtensions)];

  logger.info({
    message: "Importing dropped files",
    info: {
      num_files: allFileItems.length,
      num_filtered_in_files: importableFileItems.length,
      too_many_files: tooManyFiles,
      max_files: MAX_FILES,
      had_big_files: bigFilesCount > 0,
      num_big_files: bigFilesCount,
      filtered_out_extensions: uniqueFilteredOutExtensions,
      context,
    },
  });

  trackEvent(TrackedEvent.ImportFilesDropped, {
    num_files: allFileItems.length,
    num_filtered_in_files: importableFileItems.length,
    too_many_files: tooManyFiles,
    max_files: MAX_FILES,
    had_big_files: bigFilesCount > 0,
    num_big_files: bigFilesCount,
    filtered_out_extensions: uniqueFilteredOutExtensions,
    context,
  });

  if (tooManyFiles) {
    toastModule.triggerToast({
      content: <MaxFilesToastContent />,
    });
    return;
  }

  if (bigFilesCount > 0) {
    toastModule.triggerToast({
      content: <BigFileToastContent />,
    });
  }

  if (importableFileItems.length > 0) {
    try {
      importFilesAsSession(importableFileItems, store);
    } catch (e) {
      console.error(e);
      toastModule.triggerToast({ content: "An error occurred while importing files" });
    }
  } else {
    toastModule.triggerToast({
      content: "Only Markdown and text files are supported",
    });
  }
};

const MaxFilesToastContent = () => {
  return (
    <StyledMaxFilesToastWrapper>
      <MDSIcon kind={MDSIconKind.Warning} />
      Imports are limited to {MAX_FILES} files at a time
    </StyledMaxFilesToastWrapper>
  );
};

const BigFileToastContent = () => {
  return (
    <StyledMaxFilesToastWrapper>
      <MDSIcon kind={MDSIconKind.Warning} />
      Files larger than 30MB are left out of imports
    </StyledMaxFilesToastWrapper>
  );
};
const StyledMaxFilesToastWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing["2"]};
`;
